import { Document, Page, View, Text } from '@react-pdf/renderer';
import type { WindowConfig, CurtainStyleConfig, FabricConfig, LiningConfig, HardwareConfig, CutCalculation } from '@/types/curtain';
import { baseStyles, BRAND, fmtMm, fmtM, fmtCurrency, generateOrderNumber } from './pdfStyles';
import { getLabel } from '@/lib/enumLabels';

interface OrderConfirmationProps {
  customerName: string;
  approvedAt: Date;
  windowConfig: Partial<WindowConfig>;
  styleConfig: Partial<CurtainStyleConfig>;
  fabricConfig: Partial<FabricConfig>;
  liningConfig: Partial<LiningConfig>;
  hardwareConfig: Partial<HardwareConfig>;
  calculation: CutCalculation;
  totalCost: number;
  fabricCost: number;
  liningCost: number;
  hardwareCost: number;
}

export function OrderConfirmationPDF(props: OrderConfirmationProps) {
  const {
    customerName, approvedAt, windowConfig, styleConfig,
    fabricConfig, liningConfig, hardwareConfig, calculation,
    totalCost, fabricCost, liningCost, hardwareCost,
  } = props;

  const orderNumber = generateOrderNumber();

  return (
    <Document title={`Order Confirmation - ${orderNumber}`} author="Hail Mary Curtains">
      <Page size="A4" style={baseStyles.page}>
        {/* Header */}
        <View style={baseStyles.headerRow}>
          <View>
            <Text style={baseStyles.brandName}>Hail Mary Curtains</Text>
            <Text style={{ fontSize: 8, color: BRAND.slate500, marginTop: 2 }}>Custom Cut-to-Measure Curtains</Text>
          </View>
          <View>
            <Text style={baseStyles.docTitle}>ORDER CONFIRMATION</Text>
            <Text style={baseStyles.metaText}>{orderNumber}</Text>
            <Text style={baseStyles.metaText}>{approvedAt.toLocaleDateString()} {approvedAt.toLocaleTimeString()}</Text>
          </View>
        </View>

        {/* Customer */}
        <View style={baseStyles.kvRow}>
          <Text style={baseStyles.kvLabel}>Customer:</Text>
          <Text style={baseStyles.kvValue}>{customerName}</Text>
        </View>

        {/* Window & Mounting */}
        <Text style={baseStyles.sectionTitle}>Window & Mounting</Text>
        <View style={baseStyles.kvRow}>
          <Text style={baseStyles.kvLabel}>Room / Window:</Text>
          <Text style={baseStyles.kvValue}>{windowConfig.roomName || '-'} — {windowConfig.windowName || '-'}</Text>
        </View>
        <View style={baseStyles.kvRow}>
          <Text style={baseStyles.kvLabel}>Mount Type:</Text>
          <Text style={baseStyles.kvValue}>{getLabel('MountType', windowConfig.mountType || 'wall').toUpperCase()}</Text>
        </View>
        <View style={baseStyles.kvRow}>
          <Text style={baseStyles.kvLabel}>Track Width:</Text>
          <Text style={baseStyles.kvValue}>{fmtMm(windowConfig.trackWidth || 0)}</Text>
        </View>
        <View style={baseStyles.kvRow}>
          <Text style={baseStyles.kvLabel}>Finished Drop:</Text>
          <Text style={baseStyles.kvValue}>{fmtMm(windowConfig.finishedDrop || 0)}</Text>
        </View>
        <View style={baseStyles.kvRow}>
          <Text style={baseStyles.kvLabel}>Drop Style:</Text>
          <Text style={baseStyles.kvValue}>{getLabel('DropStyle', windowConfig.dropStyle || 'floating').toUpperCase()}</Text>
        </View>

        {/* Curtain Style */}
        <Text style={baseStyles.sectionTitle}>Curtain Configuration</Text>
        <View style={baseStyles.kvRow}>
          <Text style={baseStyles.kvLabel}>Heading Style:</Text>
          <Text style={baseStyles.kvValue}>{getLabel('HeadingStyle', styleConfig.headingStyle || 's-fold').toUpperCase()}</Text>
        </View>
        <View style={baseStyles.kvRow}>
          <Text style={baseStyles.kvLabel}>Panel Layout:</Text>
          <Text style={baseStyles.kvValue}>{styleConfig.isPair ? 'PAIR (Split)' : 'SINGLE PANEL'}</Text>
        </View>
        <View style={baseStyles.kvRow}>
          <Text style={baseStyles.kvLabel}>Fabric Type:</Text>
          <Text style={baseStyles.kvValue}>{getLabel('FabricType', fabricConfig.fabricType || 'linen').toUpperCase()}</Text>
        </View>
        <View style={baseStyles.kvRow}>
          <Text style={baseStyles.kvLabel}>Opacity:</Text>
          <Text style={baseStyles.kvValue}>{getLabel('Opacity', fabricConfig.opacity || 'light-filtering').toUpperCase()}</Text>
        </View>
        <View style={baseStyles.kvRow}>
          <Text style={baseStyles.kvLabel}>Pattern:</Text>
          <Text style={baseStyles.kvValue}>{(fabricConfig.pattern || 'solid').toUpperCase()}</Text>
        </View>
        <View style={baseStyles.kvRow}>
          <Text style={baseStyles.kvLabel}>Lining:</Text>
          <Text style={baseStyles.kvValue}>{getLabel('LiningType', liningConfig.liningType || 'standard').toUpperCase()}</Text>
        </View>
        <View style={baseStyles.kvRow}>
          <Text style={baseStyles.kvLabel}>Hardware:</Text>
          <Text style={baseStyles.kvValue}>{getLabel('HardwareStyle', hardwareConfig.style || 'architectural').toUpperCase()}</Text>
        </View>

        {/* Material Summary */}
        <Text style={baseStyles.sectionTitle}>Material Requirements</Text>
        <View style={baseStyles.kvRow}>
          <Text style={baseStyles.kvLabel}>Number of Widths:</Text>
          <Text style={baseStyles.kvValue}>{calculation.numberOfWidths}</Text>
        </View>
        <View style={baseStyles.kvRow}>
          <Text style={baseStyles.kvLabel}>Cut Drop per Width:</Text>
          <Text style={baseStyles.kvValue}>{fmtMm(calculation.cutDrop)}</Text>
        </View>
        <View style={baseStyles.kvRow}>
          <Text style={baseStyles.kvLabel}>Total Fabric:</Text>
          <Text style={baseStyles.kvValue}>{fmtM(calculation.totalFabricMeters)}</Text>
        </View>
        <View style={baseStyles.kvRow}>
          <Text style={baseStyles.kvLabel}>Total Lining:</Text>
          <Text style={baseStyles.kvValue}>{fmtM(calculation.liningMeters)}</Text>
        </View>

        {/* Pricing */}
        <Text style={baseStyles.sectionTitle}>Pricing</Text>
        <View style={baseStyles.kvRow}>
          <Text style={baseStyles.kvLabel}>Fabric Cost:</Text>
          <Text style={baseStyles.kvValue}>{fmtCurrency(fabricCost)}</Text>
        </View>
        <View style={baseStyles.kvRow}>
          <Text style={baseStyles.kvLabel}>Lining Cost:</Text>
          <Text style={baseStyles.kvValue}>{fmtCurrency(liningCost)}</Text>
        </View>
        <View style={baseStyles.kvRow}>
          <Text style={baseStyles.kvLabel}>Hardware Cost:</Text>
          <Text style={baseStyles.kvValue}>{fmtCurrency(hardwareCost)}</Text>
        </View>
        <View style={baseStyles.totalRow}>
          <Text style={baseStyles.totalLabel}>TOTAL</Text>
          <Text style={baseStyles.totalValue}>{fmtCurrency(totalCost)}</Text>
        </View>

        {/* Approval Signature */}
        <Text style={baseStyles.sectionTitle}>Approval</Text>
        <View style={{ marginTop: 8 }}>
          <View style={baseStyles.kvRow}>
            <Text style={baseStyles.kvLabel}>Approved By:</Text>
            <Text style={baseStyles.kvValue}>{customerName}</Text>
          </View>
          <View style={baseStyles.kvRow}>
            <Text style={baseStyles.kvLabel}>Approved At:</Text>
            <Text style={baseStyles.kvValue}>{approvedAt.toLocaleDateString()} {approvedAt.toLocaleTimeString()}</Text>
          </View>
        </View>
        <View style={baseStyles.noteBox}>
          <Text style={baseStyles.noteText}>
            By approving this order, the customer confirms that all measurements, style selections, and material choices
            are correct. Custom-made curtains cannot be returned or exchanged. Production will commence upon receipt of this
            confirmed order.
          </Text>
        </View>

        {/* Footer */}
        <View style={baseStyles.footer} fixed>
          <Text>Hail Mary Curtains — Customer Order Confirmation</Text>
          <Text>{orderNumber}</Text>
        </View>
      </Page>
    </Document>
  );
}
